import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { CheckCircle2, XCircle, Inbox, ArrowRight, Loader2, MapPin, Phone, Mail, Building2 } from "lucide-react";
import { formatDate } from "../lib/format";

function RejectDialog({ open, onOpenChange, project, onSubmit }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!project) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onSubmit(project.id, reason);
    setBusy(false);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md" data-testid="reject-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Reject "{project.name}"</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Reason (sent back to the sales rep)</Label>
            <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" className="rounded-sm mt-1.5" placeholder="e.g. Install date conflicts with Riverside crew — please push out 2 weeks." />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="reject-submit-btn" className="bg-red-600 hover:bg-red-700 text-white rounded-sm">
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />} Reject
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PendingApprovalsPage() {
  const { isManager } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/projects/pending");
      setPending(data);
    } catch (err) { toast.error("Could not load pending approvals"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (p) => {
    setBusyId(p.id);
    try {
      await api.post(`/projects/${p.id}/approve`);
      toast.success(`Approved "${p.name}" → Upcoming`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Approve failed"); }
    finally { setBusyId(null); }
  };
  const reject = async (id, reason) => {
    try {
      await api.post(`/projects/${id}/reject`, { reason });
      toast.success("Rejected · sales rep notified");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Reject failed"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Approvals"
        title="Pending Projects"
        description={isManager ? "Sales submissions waiting for your green light before hitting the calendar." : "Projects you've submitted, awaiting Owner / Installation Manager approval."}
        testId="pending-header"
      />
      <div className="px-4 md:px-8 pt-6">
        {pending.length === 0 ? (
          <div className="border border-dashed border-slate-300 bg-white rounded-md p-12 text-center" data-testid="pending-empty">
            <Inbox size={40} className="mx-auto text-slate-400" />
            <div className="mt-3 font-display text-xl font-bold text-slate-900">Inbox zero</div>
            <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">
              No submissions right now. New sales conversions will land here for approval.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((p) => (
              <div key={p.id} className="border border-amber-200 bg-amber-50/40 rounded-md p-4 shadow-sm" data-testid={`pending-row-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-500 text-white">
                        Pending
                      </span>
                      <span className="font-display text-lg font-black tracking-tight text-slate-900">{p.name}</span>
                    </div>
                    <div className="mt-1 text-sm text-slate-700 inline-flex items-center gap-2">
                      <Building2 size={14} className="text-slate-400" /> {p.client || "—"}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 font-mono">
                      {p.contact_name && <span><Phone size={11} className="inline mr-1 text-slate-400" />{p.contact_phone || "—"} · {p.contact_name}</span>}
                      {p.contact_email && <span><Mail size={11} className="inline mr-1 text-slate-400" />{p.contact_email}</span>}
                      {(p.city || p.state) && <span><MapPin size={11} className="inline mr-1 text-slate-400" />{p.city}{p.state && `, ${p.state}`}</span>}
                      {p.estimated_value && <span className="font-bold text-slate-800">${Number(p.estimated_value).toLocaleString()}</span>}
                      {p.expected_install_date && <span>Install {p.expected_install_date}</span>}
                    </div>
                    {p.notes && <div className="mt-2 text-xs text-slate-600 italic border-l-2 border-slate-300 pl-2 whitespace-pre-wrap max-h-32 overflow-y-auto">{p.notes}</div>}
                    <div className="mt-2 text-xs text-slate-500">Submitted by <strong className="text-slate-800">{p.sales_rep_name || "—"}</strong> · {formatDate(p.submitted_at || p.created_at)}</div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 shrink-0">
                    <Button onClick={() => navigate(`/projects/${p.id}`)} variant="outline" size="sm" data-testid={`pending-open-${p.id}`} className="rounded-sm">
                      Open <ArrowRight size={14} className="ml-1" />
                    </Button>
                    {isManager && (
                      <>
                        <Button onClick={() => setRejectTarget(p)} variant="outline" size="sm" data-testid={`pending-reject-${p.id}`} className="rounded-sm border-red-300 text-red-700 hover:bg-red-50">
                          <XCircle size={14} className="mr-1" /> Reject
                        </Button>
                        <Button onClick={() => approve(p)} size="sm" disabled={busyId === p.id} data-testid={`pending-approve-${p.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
                          {busyId === p.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
                          Approve
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <RejectDialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)} project={rejectTarget} onSubmit={reject} />
    </div>
  );
}
